import React, { useState, useEffect } from 'react';
import { Modal, Select, Input, Button, Typography, Checkbox, Form, Radio } from 'antd';
import { Mail, X, Send } from 'lucide-react';
import dialerService from '../services/dialerService';
import { getSession } from '../../../getSession';

const { Text } = Typography;
const { TextArea } = Input;

const EmailModalV2 = ({ isOpen, onClose, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [fromMails, setFromMails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emailMode, setEmailMode] = useState('Template');

  useEffect(() => {
    if (isOpen && record) {
      setLeadEmails([]);
      fetchInitialData();
      fetchLeadData();
      form.resetFields();
    }
  }, [isOpen, record, form]);

  const fetchLeadData = async () => {
    // Immediate fallback from record
    const fallbackEmails = [];
    if (record.EmailID) {
      fallbackEmails.push({ label: record.EmailID, value: record.EmailID });
      setLeadEmails(fallbackEmails);
      form.setFieldsValue({ selectedEmails: [record.EmailID] });
    }

    try {
      const lead = await dialerService.getLeadById(record.Id);
      if (lead) {
        const emails = [];
        if (lead.EmailID) emails.push({ label: lead.EmailID, value: lead.EmailID });
        if (lead.EmailID1) emails.push({ label: lead.EmailID1, value: lead.EmailID1 });
        if (lead.EmailID2) emails.push({ label: lead.EmailID2, value: lead.EmailID2 });
        
        if (emails.length > 0) {
          setLeadEmails(emails);
          form.setFieldsValue({
            selectedEmails: emails.map(e => e.value)
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch lead data:', error);
    }
  };

  const [leadEmails, setLeadEmails] = useState([]);

  const fetchInitialData = async () => {
    try {
      const [accRes, mailRes, tempRes] = await Promise.all([
        dialerService.getEmailAccounts(),
        dialerService.getFromMailIds(),
        dialerService.getEmailTemplates()
      ]);
      
      if (accRes) setAccounts(accRes.map(a => ({ label: a.Text, value: String(a.Code || a.Value || a.Id) })));
      if (mailRes) setFromMails(mailRes.map(m => ({ label: m.Text, value: String(m.Value || m.Code || m.Id) })));
      if (tempRes) setTemplates(tempRes.map(t => ({ label: t.Text, value: String(t.Code || t.Value || t.Id) })));
    } catch (error) {
      console.error('Failed to fetch email data:', error);
    }
  };

  const handleTemplateChange = async (value) => {
    try {
      const res = await dialerService.getTemplateSubject(value);
      if (res) {
        form.setFieldsValue({ subject: res });
      }
    } catch (error) {
      console.error('Failed to fetch template subject:', error);
    }
  };

  const handleSend = async (values) => {
    setLoading(true);
    try {
      const emailData = {
        EnquiryLeadId: record.Id,
        EntityType: record.EntityName || 'Lead',
        Account: values.account,
        SenderName: accounts.find(a => a.value === values.account)?.label,
        EmailType: values.emailMode,
        Email: values.selectedEmails[0] || '',
        Email1: values.selectedEmails[1] || '',
        Email2: values.selectedEmails[2] || '',
        Subject: values.subject,
        Message: values.emailMode === 'Compose' ? values.message : '',
        Template: values.emailMode === 'Template' ? values.template : '',
        ReplyTo: values.fromMail,
        FromMailID: values.fromMail
      };

      const res = await dialerService.sendEmail(emailData);
      if (res.Details) alert(res.Details);
      onClose();
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-indigo-600" />
          <span className="text-lg font-bold text-slate-800">Send Mail</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSend}
        className="py-4"
      >
        <div className="space-y-4">
          <Form.Item
            name="selectedEmails"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Choose Email ID</span>}
            rules={[{ required: true, message: 'Select at least one email' }]}
            className="mb-6"
          >
            <Checkbox.Group className="w-full">
              <div className="grid grid-cols-1 gap-2">
                {leadEmails.map(e => (
                  <div key={e.value} className="flex items-center p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-colors cursor-pointer group">
                    <Checkbox value={e.value} className="text-slate-700 font-bold w-full">{e.label}</Checkbox>
                  </div>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="account"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Account</span>}
              rules={[{ required: true, message: 'Select account' }]}
            >
              <Select placeholder="Select Account" options={accounts} className="h-11" getPopupContainer={trigger => trigger.parentNode} />
            </Form.Item>

            <Form.Item
              name="fromMail"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> From Mail ID</span>}
              rules={[{ required: true, message: 'Select from mail' }]}
            >
              <Select placeholder="Select From Mail" options={fromMails} className="h-11" getPopupContainer={trigger => trigger.parentNode} />
            </Form.Item>
          </div>

          <Form.Item name="emailMode">
            <Radio.Group onChange={(e) => setEmailMode(e.target.value)} value={emailMode} className="premium-radio-group">
              <Radio.Button value="Template">Select Template</Radio.Button>
              <Radio.Button value="Compose">Compose Mail</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="subject"
            label={<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Subject</span>}
            rules={[{ required: true, message: 'Enter subject' }]}
          >
            <Input placeholder="Enter email subject" className="h-11 rounded-xl" />
          </Form.Item>

          {emailMode === 'Template' ? (
            <Form.Item
              name="template"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Select Template</span>}
              rules={[{ required: true, message: 'Select a template' }]}
            >
              <Select 
                placeholder="Choose a template" 
                options={templates} 
                className="h-11" 
                onChange={handleTemplateChange} 
                getPopupContainer={trigger => trigger.parentNode}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="message"
              label={<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Message</span>}
              rules={[{ required: true, message: 'Enter message' }]}
            >
              <TextArea rows={5} placeholder="Write your email here..." className="rounded-xl" />
            </Form.Item>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button onClick={onClose} className="rounded-xl font-bold h-11 px-6">
            Cancel
          </Button>
          <Button 
            type="primary" 
            htmlType="submit"
            loading={loading}
            icon={<Send size={16} />}
            className="rounded-xl font-bold h-11 px-10 bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200 border-none flex items-center gap-2"
          >
            Send Email
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EmailModalV2;
